import type {
  BrokerageAccount,
  AccountActivity,
  AccountHolding,
  DateRange,
} from './brokerage.types.ts';

const VISITOR_ID_KEY = 'dyr_visitor_id';
const VISITOR_ID_QUERY_KEYS = ['dyr_visitor_id', 'visitorId'] as const;
const PLAID_ITEMS_KEY = 'dyr_plaid_items';
const PLAID_LINK_TOKEN_KEY = 'dyr_plaid_link_token';
const PLAID_LINK_SESSION_KEY = 'dyr_plaid_link_session';
const PLAID_TRANSACTIONS_CONSENT_ITEMS_KEY = 'dyr_plaid_transactions_consent_items';
const TRANSFER_DEBUG_KEY = 'dyr_debug_transfers';
const PLAID_ADDITIONAL_CONSENT_REQUIRED = 'ADDITIONAL_CONSENT_REQUIRED';

declare const CONFIG: { WORKER_BASE_URL: string; DEFAULT_PLAID_VISITOR_ID?: string };
declare const Plaid: {
  create: (config: {
    token: string;
    receivedRedirectUri?: string;
    onSuccess: (publicToken: string | null, metadata: unknown) => void;
    onExit: (err: unknown, metadata: unknown) => void;
    onEvent: (eventName: string, metadata: unknown) => void;
  }) => { open: () => void; destroy: () => void };
};

type PlaidLinkSessionMode = 'connect' | 'update-transactions';

interface PlaidLinkSession {
  linkToken: string;
  visitorId: string;
  mode: PlaidLinkSessionMode;
  itemId?: string;
  institutionName?: string;
}

export function getStoredVisitorId(): string | null {
  return localStorage.getItem(VISITOR_ID_KEY) || getDefaultVisitorId();
}

export function setStoredVisitorId(visitorId: string): void {
  localStorage.setItem(VISITOR_ID_KEY, visitorId);
}

function isValidVisitorId(visitorId: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(visitorId);
}

function getDefaultVisitorId(): string | null {
  const visitorId = CONFIG.DEFAULT_PLAID_VISITOR_ID?.trim();
  if (!visitorId) return null;
  if (isValidVisitorId(visitorId)) return visitorId;

  console.warn('[DYR Plaid] Ignoring invalid configured default visitor ID.');
  return null;
}

function clearVisitorScopedBrowserState(): void {
  localStorage.removeItem(PLAID_ITEMS_KEY);
  localStorage.removeItem(PLAID_TRANSACTIONS_CONSENT_ITEMS_KEY);
  localStorage.removeItem('dyr_selected_account');
  localStorage.removeItem('dyr_account_names');
  localStorage.removeItem('dyr_summary_excluded_account_ids');
  localStorage.removeItem('dyr_summary_holdings_cache_v1');
}

function removeVisitorIdFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;

  for (const key of VISITOR_ID_QUERY_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }
}

export function applyVisitorIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const visitorId = VISITOR_ID_QUERY_KEYS
    .map(key => params.get(key)?.trim())
    .find((value): value is string => Boolean(value));

  if (!visitorId) return getStoredVisitorId();

  if (!isValidVisitorId(visitorId)) {
    console.warn('[DYR Plaid] Ignoring invalid visitor ID URL parameter.');
    removeVisitorIdFromUrl();
    return getStoredVisitorId();
  }

  const existingVisitorId = getStoredVisitorId();
  if (existingVisitorId !== visitorId) {
    clearVisitorScopedBrowserState();
    setStoredVisitorId(visitorId);
  }

  removeVisitorIdFromUrl();
  return visitorId;
}

// Store linked item IDs locally for quick access
function getStoredItemIds(): string[] {
  const raw = localStorage.getItem(PLAID_ITEMS_KEY);
  return raw ? JSON.parse(raw) : [];
}

function addStoredItemId(itemId: string): void {
  const items = getStoredItemIds();
  if (!items.includes(itemId)) {
    items.push(itemId);
    localStorage.setItem(PLAID_ITEMS_KEY, JSON.stringify(items));
  }
}

function removeStoredItemId(itemId: string): void {
  const items = getStoredItemIds().filter(id => id !== itemId);
  localStorage.setItem(PLAID_ITEMS_KEY, JSON.stringify(items));
}

function getTransactionsConsentItemIds(): string[] {
  const raw = localStorage.getItem(PLAID_TRANSACTIONS_CONSENT_ITEMS_KEY);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function hasPlaidTransactionsConsent(itemId: string): boolean {
  return getTransactionsConsentItemIds().includes(itemId);
}

function markPlaidTransactionsConsent(itemId: string): void {
  const itemIds = getTransactionsConsentItemIds();
  if (!itemIds.includes(itemId)) {
    itemIds.push(itemId);
    localStorage.setItem(PLAID_TRANSACTIONS_CONSENT_ITEMS_KEY, JSON.stringify(itemIds));
  }
}

function clearPlaidTransactionsConsent(itemId: string): void {
  const itemIds = getTransactionsConsentItemIds().filter(id => id !== itemId);
  localStorage.setItem(PLAID_TRANSACTIONS_CONSENT_ITEMS_KEY, JSON.stringify(itemIds));
}

async function workerPost<T>(path: string, body?: object): Promise<T> {
  const response = await fetch(`${CONFIG.WORKER_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

async function workerGet<T>(path: string): Promise<T> {
  const response = await fetch(`${CONFIG.WORKER_BASE_URL}${path}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

async function workerDelete<T>(path: string): Promise<T> {
  const response = await fetch(`${CONFIG.WORKER_BASE_URL}${path}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Plaid Link Integration
// ============================================================================

interface LinkTokenResponse {
  linkToken: string;
  expiration: string;
  visitorId: string;
  itemId?: string;
  institutionName?: string;
}

interface ExchangeTokenResponse {
  success: boolean;
  itemId: string;
  institutionName: string;
}

interface TransactionsConsentStatusResponse {
  enabled: boolean;
  ready: boolean;
  itemId: string;
  institutionName: string;
  error?: string;
  plaidErrorCode?: string | null;
}

function isPlaidOAuthRedirect(): boolean {
  return new URLSearchParams(window.location.search).has('oauth_state_id');
}

function clearPlaidOAuthQuery(): void {
  if (!isPlaidOAuthRedirect()) return;

  window.history.replaceState({}, document.title, `${window.location.origin}${window.location.pathname}`);
}

function savePlaidLinkSession(session: PlaidLinkSession): void {
  sessionStorage.setItem(PLAID_LINK_SESSION_KEY, JSON.stringify(session));
  sessionStorage.setItem(PLAID_LINK_TOKEN_KEY, session.linkToken);
}

function readPlaidLinkSession(): PlaidLinkSession | null {
  const raw = sessionStorage.getItem(PLAID_LINK_SESSION_KEY);
  if (raw) {
    try {
      const session = JSON.parse(raw) as PlaidLinkSession;
      if (session.linkToken && session.visitorId && session.mode) {
        return session;
      }
    } catch {
      // Fall through to legacy session handling below.
    }
  }

  const visitorId = getStoredVisitorId();
  const linkToken = sessionStorage.getItem(PLAID_LINK_TOKEN_KEY);
  if (!visitorId || !linkToken) return null;

  return { linkToken, visitorId, mode: 'connect' };
}

function clearPlaidLinkSession(): void {
  sessionStorage.removeItem(PLAID_LINK_SESSION_KEY);
  sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
}

async function exchangePublicToken(
  publicToken: string,
  visitorId: string
): Promise<{ itemId: string; institutionName: string }> {
  const result = await workerPost<ExchangeTokenResponse>('/plaid/exchange-token', {
    publicToken,
    visitorId,
  });

  addStoredItemId(result.itemId);
  clearPlaidLinkSession();

  return {
    itemId: result.itemId,
    institutionName: result.institutionName,
  };
}

async function openPlaidLinkWithToken(
  linkToken: string,
  visitorId: string,
  receivedRedirectUri?: string,
  session: PlaidLinkSession = { linkToken, visitorId, mode: 'connect' }
): Promise<{ itemId: string; institutionName: string }> {
  if (typeof Plaid === 'undefined') {
    throw new Error('Plaid Link SDK failed to load');
  }

  return new Promise((resolve, reject) => {
    const handler = Plaid.create({
      token: linkToken,
      ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
      onSuccess: async (publicToken: string | null) => {
        try {
          let result: { itemId: string; institutionName: string };

          if (session.mode === 'update-transactions') {
            if (!session.itemId) {
              throw new Error('Missing Plaid Item for Transactions consent update.');
            }

            clearPlaidLinkSession();
            result = {
              itemId: session.itemId,
              institutionName: session.institutionName || 'Connected account',
            };
          } else {
            if (!publicToken) {
              throw new Error('Missing Plaid public token.');
            }

            result = await exchangePublicToken(publicToken, visitorId);
          }

          clearPlaidOAuthQuery();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      },
      onExit: (err) => {
        clearPlaidOAuthQuery();

        if (err) {
          reject(new Error('Plaid Link closed with error'));
        } else {
          reject(new Error('Plaid Link closed by user'));
        }
      },
      onEvent: (eventName) => {
        console.log('Plaid Link event:', eventName);
      },
    });

    handler.open();
  });
}

/**
 * Open Plaid Link to connect a new brokerage
 */
export async function openPlaidLink(): Promise<{ itemId: string; institutionName: string }> {
  // Get or create visitor ID
  let visitorId = getStoredVisitorId();
  if (!visitorId) {
    visitorId = crypto.randomUUID();
    setStoredVisitorId(visitorId);
  }

  // Omit redirect_uri until a canonical Plaid OAuth callback is allowlisted.
  // Desktop web can use Plaid's popup/new-tab OAuth flow without it.
  const { linkToken } = await workerPost<LinkTokenResponse>('/plaid/link-token', {
    visitorId,
  });
  savePlaidLinkSession({ linkToken, visitorId, mode: 'connect' });

  return openPlaidLinkWithToken(linkToken, visitorId);
}

export async function openPlaidTransactionsConsent(
  itemId: string
): Promise<{ itemId: string; institutionName: string }> {
  const visitorId = getStoredVisitorId();
  if (!visitorId) {
    throw new Error('Connect a brokerage account before enabling transfer history.');
  }

  const { linkToken, institutionName } = await workerPost<LinkTokenResponse>('/plaid/transactions-link-token', {
    visitorId,
    itemId,
  });
  const session: PlaidLinkSession = {
    linkToken,
    visitorId,
    mode: 'update-transactions',
    itemId,
    institutionName,
  };

  savePlaidLinkSession(session);
  const result = await openPlaidLinkWithToken(linkToken, visitorId, undefined, session);
  const status = await getPlaidTransactionsConsentStatus(visitorId, itemId);

  if (!status.enabled) {
    clearPlaidTransactionsConsent(itemId);
    throw new Error(formatPlaidTransactionsConsentError(status));
  }

  markPlaidTransactionsConsent(itemId);
  return result;
}

async function getPlaidTransactionsConsentStatus(
  visitorId: string,
  itemId: string
): Promise<TransactionsConsentStatusResponse> {
  const params = new URLSearchParams({ visitorId, itemId });
  return workerGet<TransactionsConsentStatusResponse>(`/plaid/transactions-consent-status?${params}`);
}

function formatPlaidTransactionsConsentError(status: TransactionsConsentStatusResponse): string {
  if (status.plaidErrorCode === PLAID_ADDITIONAL_CONSENT_REQUIRED) {
    return 'Plaid did not grant Transactions consent for this connection. Try enabling transfer history again, or reconnect Robinhood and approve the Transactions data permission if Plaid shows it.';
  }

  if (status.plaidErrorCode === 'PRODUCTS_NOT_SUPPORTED' || status.plaidErrorCode === 'PRODUCT_NOT_SUPPORTED') {
    return 'Plaid says Transactions is not supported for this connection. Transfer history will need to come from investment activity instead of the Transactions product.';
  }

  return status.error || 'Plaid did not confirm Transactions access for this connection.';
}

export async function completePlaidOAuthRedirect(): Promise<{ itemId: string; institutionName: string } | null> {
  if (!isPlaidOAuthRedirect()) {
    return null;
  }

  const session = readPlaidLinkSession();

  if (!session) {
    clearPlaidOAuthQuery();
    throw new Error('Missing saved Plaid Link session. Please connect your brokerage again.');
  }

  return openPlaidLinkWithToken(session.linkToken, session.visitorId, window.location.href, session);
}

// ============================================================================
// Accounts
// ============================================================================

interface PlaidAccount {
  id: string;
  itemId: string;
  name: string;
  officialName: string | null;
  type: string;
  subtype: string;
  mask: string;
  institutionName: string;
  balance: number;
  currency: string;
}

interface AccountsResponse {
  accounts: PlaidAccount[];
}

export async function getAccounts(visitorId: string): Promise<BrokerageAccount[]> {
  const result = await workerGet<AccountsResponse>(
    `/plaid/accounts?visitorId=${encodeURIComponent(visitorId)}`
  );

  // Normalize to BrokerageAccount format
  return result.accounts.map(acct => ({
    id: acct.id,
    brokerageAuthorizationId: acct.itemId, // Use itemId as authorization ID
    name: acct.officialName || acct.name,
    number: acct.mask,
    institutionName: acct.institutionName,
    syncStatus: 'OK',
    balance: acct.balance,
    currency: acct.currency,
  }));
}

// ============================================================================
// Holdings
// ============================================================================

interface PlaidHolding {
  accountId: string;
  symbol: string | null;
  name: string | null;
  securityType: string;
  isOption: boolean;
  isCash?: boolean;
  optionSymbol: string | null;
  underlyingSymbol: string | null;
  strikePrice: number | null;
  expirationDate: string | null;
  optionType: 'CALL' | 'PUT' | null;
  units: number;
  currentPrice: number;
  priceAsOf: string;
  averagePurchasePrice: number | null;
  costBasis: number | null;
  marketValue: number;
  currency: string;
}

interface HoldingsResponse {
  holdings: PlaidHolding[];
  accounts: unknown[];
}

export interface PortfolioSnapshotHolding {
  symbol: string;
  name: string;
  type: 'equity' | 'option' | 'cash' | 'adjustment';
  quantity: number;
  value: number;
  markSource?: 'broker_current';
  markConfidence?: 'high';
  markDate?: string;
}

export interface PortfolioNavSnapshot {
  date: string;
  capturedAt: string;
  source: 'broker_snapshot';
  selectionKey: string;
  selectedAccountIds: string[];
  value: number;
  cash: number;
  totalMV: number;
  holdings: PortfolioSnapshotHolding[];
}

interface PortfolioSnapshotsResponse {
  snapshot?: PortfolioNavSnapshot;
  snapshots: PortfolioNavSnapshot[];
  selectionKey: string;
}

export async function getHoldings(
  visitorId: string,
  accountId: string
): Promise<AccountHolding[]> {
  // Find the itemId for this account
  const accounts = await getAccounts(visitorId);
  const account = accounts.find(a => a.id === accountId);
  if (!account) {
    throw new Error('Account not found');
  }

  return getHoldingsForAccount(visitorId, account);
}

export async function getHoldingsForAccount(
  visitorId: string,
  account: BrokerageAccount
): Promise<AccountHolding[]> {
  const result = await workerGet<HoldingsResponse>(
    `/plaid/holdings?visitorId=${encodeURIComponent(visitorId)}&itemId=${encodeURIComponent(account.brokerageAuthorizationId)}&accountId=${encodeURIComponent(account.id)}`
  );

  return result.holdings.map(normalizePlaidHolding);
}

export async function getHoldingsForItem(
  visitorId: string,
  itemId: string
): Promise<AccountHolding[]> {
  const result = await workerGet<HoldingsResponse>(
    `/plaid/holdings?visitorId=${encodeURIComponent(visitorId)}&itemId=${encodeURIComponent(itemId)}`
  );

  return result.holdings.map(normalizePlaidHolding);
}

export async function savePortfolioNavSnapshot(
  visitorId: string,
  snapshot: Omit<PortfolioNavSnapshot, 'source'>
): Promise<PortfolioNavSnapshot[]> {
  const result = await workerPost<PortfolioSnapshotsResponse>('/plaid/portfolio-snapshots', {
    visitorId,
    snapshot,
  });
  return result.snapshots || [];
}

export async function getPortfolioNavSnapshots(
  visitorId: string,
  selectedAccountIds: string[],
  days = 370
): Promise<PortfolioNavSnapshot[]> {
  const params = new URLSearchParams({
    visitorId,
    selectionKey: 'all',
    selectedAccountIds: [...selectedAccountIds].sort().join(','),
    days: String(days),
  });
  const result = await workerGet<PortfolioSnapshotsResponse>(`/plaid/portfolio-snapshots?${params}`);
  return result.snapshots || [];
}

function normalizePlaidHolding(h: PlaidHolding): AccountHolding {
  const marketValue = h.isOption && h.units < 0
    ? -Math.abs(h.marketValue)
    : h.marketValue;
  const unrealizedPnl = h.costBasis != null && h.costBasis !== 0
    ? marketValue - h.costBasis
    : null;

  return {
    accountId: h.accountId,
    symbol: h.symbol || h.optionSymbol || 'UNKNOWN',
    optionSymbol: h.optionSymbol,
    underlyingSymbol: h.underlyingSymbol || h.symbol,
    description: h.name,
    strikePrice: h.strikePrice,
    expirationDate: h.expirationDate,
    optionType: h.optionType,
    units: h.units,
    averagePurchasePrice: h.averagePurchasePrice,
    costBasis: h.costBasis,
    currentPrice: h.currentPrice,
    marketValue,
    unrealizedPnl,
    unrealizedPnlPercent: unrealizedPnl != null && h.costBasis != null && h.costBasis !== 0
      ? (unrealizedPnl / Math.abs(h.costBasis) * 100)
      : null,
    currency: h.currency,
    isOption: h.isOption,
    isCash: h.isCash,
  };
}

// ============================================================================
// Activities (Transactions)
// ============================================================================

interface PlaidTransaction {
  id: string;
  accountId: string;
  symbol: string | null;
  name: string | null;
  securityType: string;
  isOption: boolean;
  optionSymbol: string | null;
  underlyingSymbol: string | null;
  optionType: 'CALL' | 'PUT' | null;
  type: string;
  subtype: string;
  tradeDate: string;
  units: number;
  price: number;
  amount: number;
  fees: number;
  currency: string;
}

interface TransactionsResponse {
  transactions: PlaidTransaction[];
  accounts: unknown[];
  totalTransactions: number;
}

interface PlaidCashTransaction {
  id: string;
  accountId: string;
  accountName: string | null;
  accountType: string | null;
  accountSubtype: string | null;
  type: 'DEPOSIT' | 'WITHDRAWAL';
  date: string;
  authorizedDate: string | null;
  name: string | null;
  merchantName: string | null;
  amount: number;
  rawAmount: number;
  direction: 'inflow' | 'outflow';
  pending: boolean;
  paymentChannel: string | null;
  category: string[];
  personalFinanceCategory: { primary?: string; detailed?: string } | null;
  isoCurrencyCode: string;
  classificationReason?: string | null;
}

interface PlaidLargeCashTransactionDiagnostic {
  date: string;
  amount: number;
  rawAmount: number;
  direction: 'inflow' | 'outflow';
  accountName: string | null;
  accountType: string | null;
  accountSubtype: string | null;
  category: string[];
  personalFinanceCategory: { primary?: string; detailed?: string } | null;
  classifiedAsTransfer: boolean;
  classificationReason?: string | null;
}

interface CashTransactionsResponse {
  transactions: PlaidCashTransaction[];
  largeTransactions?: PlaidLargeCashTransactionDiagnostic[];
  accounts: unknown[];
  totalTransactions: number;
  candidateTransactions: number;
  unavailable?: boolean;
  error?: string;
  plaidErrorCode?: string | null;
}

export async function getActivities(
  visitorId: string,
  accountId: string,
  dateRange?: DateRange
): Promise<AccountActivity[]> {
  // Find the itemId for this account
  const accounts = await getAccounts(visitorId);
  const account = accounts.find(a => a.id === accountId);
  if (!account) {
    throw new Error('Account not found');
  }

  return getActivitiesForAccount(visitorId, account, dateRange);
}

export async function getActivitiesForAccount(
  visitorId: string,
  account: BrokerageAccount,
  dateRange?: DateRange
): Promise<AccountActivity[]> {
  let path = `/plaid/transactions?visitorId=${encodeURIComponent(visitorId)}&itemId=${encodeURIComponent(account.brokerageAuthorizationId)}&accountId=${encodeURIComponent(account.id)}`;

  if (dateRange?.startDate) {
    path += `&startDate=${dateRange.startDate}`;
  }
  if (dateRange?.endDate) {
    path += `&endDate=${dateRange.endDate}`;
  }

  const result = await workerGet<TransactionsResponse>(path);

  // Normalize to AccountActivity format
  const activities = result.transactions.map(txn => ({
    id: txn.id,
    externalReferenceId: txn.id,
    accountId: txn.accountId,
    symbol: txn.symbol,
    optionSymbol: txn.optionSymbol,
    underlyingSymbol: txn.underlyingSymbol || txn.symbol,
    type: mapPlaidTypeToActivityType(txn.type, txn.subtype, txn.amount, txn.units),
    optionType: null,
    tradeDate: txn.tradeDate,
    settlementDate: null,
    units: txn.units,
    price: txn.price,
    amount: txn.amount,
    currency: txn.currency,
    description: `${txn.type} ${txn.subtype}`.trim(),
    fee: txn.fees,
    institution: null,
  }));

  logPlaidActivityMapping(account, dateRange, result.transactions, activities);

  return activities;
}

export async function getCashTransactionsForItem(
  visitorId: string,
  itemId: string,
  dateRange?: DateRange
): Promise<AccountActivity[]> {
  let path = `/plaid/cash-transactions?visitorId=${encodeURIComponent(visitorId)}&itemId=${encodeURIComponent(itemId)}`;

  if (dateRange?.startDate) {
    path += `&startDate=${dateRange.startDate}`;
  }
  if (dateRange?.endDate) {
    path += `&endDate=${dateRange.endDate}`;
  }

  const result = await workerGet<CashTransactionsResponse>(path);
  if (result.unavailable) {
    const unavailableDetails = {
      itemId,
      error: result.error,
      plaidErrorCode: result.plaidErrorCode,
    };
    if (result.plaidErrorCode === PLAID_ADDITIONAL_CONSENT_REQUIRED) {
      clearPlaidTransactionsConsent(itemId);
    }
    if (shouldLogTransferDebug()) {
      console.info('[DYR transfer debug] Plaid cash transactions unavailable', unavailableDetails);
      console.info('[DYR transfer debug JSON] Plaid cash transactions unavailable', JSON.stringify(unavailableDetails));
    }
    return [];
  }

  markPlaidTransactionsConsent(itemId);

  const activities = result.transactions.map(txn => ({
    id: `cash:${txn.id}`,
    externalReferenceId: txn.id,
    accountId: txn.accountId,
    symbol: null,
    optionSymbol: null,
    underlyingSymbol: null,
    type: txn.type,
    optionType: null,
    tradeDate: txn.date,
    settlementDate: null,
    units: null,
    price: null,
    amount: Math.abs(txn.amount),
    currency: txn.isoCurrencyCode || 'USD',
    description: txn.name || txn.merchantName || `${txn.type} cash transfer`,
    fee: null,
    institution: txn.accountName,
  }));

  logPlaidCashTransactions(itemId, dateRange, result, activities);

  return activities;
}

export function mapPlaidTypeToActivityType(
  type: string,
  subtype: string,
  amount = 0,
  units = 0
): string {
  // Map Plaid transaction types to our activity types
  // Plaid types: buy, sell, cancel, cash, fee, transfer
  // Plaid subtypes: account_fee, adjustment, assignment, buy, contribution, deposit, etc.

  const subtypeLower = normalizePlaidTransactionValue(subtype);
  const typeLower = normalizePlaidTransactionValue(type);
  const signedAmount = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
  const signedUnits = typeof units === 'number' && Number.isFinite(units) ? units : 0;

  if (typeLower === 'buy' || subtypeLower === 'buy') return 'BUY';
  if (typeLower === 'sell' || subtypeLower === 'sell') return 'SELL';
  if (subtypeLower === 'short' || subtypeLower === 'sell short') return 'SELL';
  if (subtypeLower === 'cover' || subtypeLower === 'buy to cover') return 'BUY';
  if (subtypeLower === 'assignment') return 'ASSIGNMENT';
  if (subtypeLower === 'exercise') return 'EXERCISE';
  if (subtypeLower === 'expire') return 'EXPIRATION';
  if (subtypeLower === 'dividend') return 'DIVIDEND';
  if (subtypeLower === 'qualified dividend') return 'DIVIDEND';
  if (subtypeLower === 'non qualified dividend') return 'DIVIDEND';
  if (subtypeLower === 'non resident tax') return 'TAX';
  if (subtypeLower === 'tax' || subtypeLower === 'tax withheld') return 'TAX';
  if (subtypeLower === 'interest') return 'INTEREST';
  if (subtypeLower === 'deposit' || subtypeLower === 'contribution' || subtypeLower === 'pending credit' || subtypeLower === 'request') return 'DEPOSIT';
  if (subtypeLower === 'withdrawal' || subtypeLower === 'distribution' || subtypeLower === 'pending debit' || subtypeLower === 'send' || subtypeLower === 'loan payment') return 'WITHDRAWAL';
  if (subtypeLower === 'fee' || subtypeLower === 'account fee' || subtypeLower === 'transfer fee' || subtypeLower === 'fund fee' || subtypeLower === 'legal fee' || subtypeLower === 'management fee' || subtypeLower === 'miscellaneous fee' || subtypeLower === 'trust fee') return 'FEE';

  if (typeLower === 'cash') {
    // Plaid investment cash inflows are negative and outflows are positive.
    if (signedAmount < 0) return 'DEPOSIT';
    if (signedAmount > 0) return 'WITHDRAWAL';
  }

  if (typeLower === 'transfer' || subtypeLower === 'transfer') {
    if (signedUnits < 0 || signedAmount > 0) return 'EXTERNAL_ASSET_TRANSFER_OUT';
    if (signedUnits > 0 || signedAmount < 0) return 'EXTERNAL_ASSET_TRANSFER_IN';
    return 'TRANSFER';
  }

  return type.toUpperCase() || 'OTHER';
}

function normalizePlaidTransactionValue(value: string): string {
  return (value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function logPlaidActivityMapping(
  account: BrokerageAccount,
  dateRange: DateRange | undefined,
  transactions: PlaidTransaction[],
  activities: AccountActivity[]
): void {
  if (!shouldLogTransferDebug()) return;

  const rawBuckets = new Map<string, number>();
  const mappedBuckets = new Map<string, number>();
  const candidateRows: Array<Record<string, unknown>> = [];

  transactions.forEach((txn, index) => {
    const rawKey = `${txn.type || 'unknown'} / ${txn.subtype || 'unknown'}`;
    rawBuckets.set(rawKey, (rawBuckets.get(rawKey) || 0) + 1);

    const activity = activities[index];
    const mappedType = String(activity?.type || 'unknown');
    mappedBuckets.set(mappedType, (mappedBuckets.get(mappedType) || 0) + 1);

    if (isPotentialTransferActivity(txn, activity)) {
      candidateRows.push({
        date: txn.tradeDate,
        rawType: txn.type,
        rawSubtype: txn.subtype,
        mappedType,
        symbol: txn.symbol || txn.optionSymbol || null,
        units: txn.units,
        price: txn.price,
        amount: txn.amount,
      });
    }
  });

  console.groupCollapsed(
    `[DYR transfer debug] Plaid activities ${account.institutionName || 'brokerage'} ${account.name || ''} ${maskAccount(account.number)}`
  );
  console.log('Date range', dateRange || null);
  console.log('Transactions returned', transactions.length);
  console.log('[DYR transfer debug JSON] raw buckets', JSON.stringify(mapToSortedRows(rawBuckets, 'rawTypeSubtype')));
  console.log('[DYR transfer debug JSON] mapped buckets', JSON.stringify(mapToSortedRows(mappedBuckets, 'mappedType')));
  console.log('[DYR transfer debug JSON] transfer candidates', JSON.stringify(candidateRows.slice(0, 80)));
  console.table(mapToSortedRows(rawBuckets, 'rawTypeSubtype'));
  console.table(mapToSortedRows(mappedBuckets, 'mappedType'));
  console.log('Potential transfer/cash candidates', candidateRows.length);
  console.table(candidateRows.slice(0, 80));
  if (candidateRows.length > 80) {
    console.log(`Showing first 80 of ${candidateRows.length} candidate rows`);
  }
  console.groupEnd();
}

function logPlaidCashTransactions(
  itemId: string,
  dateRange: DateRange | undefined,
  result: CashTransactionsResponse,
  activities: AccountActivity[]
): void {
  if (!shouldLogTransferDebug()) return;

  const rows = result.transactions.map((txn, index) => ({
    date: txn.date,
    mappedType: activities[index]?.type || txn.type,
    amount: txn.amount,
    rawAmount: txn.rawAmount,
    direction: txn.direction,
    classificationReason: txn.classificationReason || '',
    accountName: txn.accountName,
    accountType: txn.accountType,
    accountSubtype: txn.accountSubtype,
    name: txn.name,
    category: txn.category?.join(' / ') || '',
    personalFinanceCategory: [
      txn.personalFinanceCategory?.primary,
      txn.personalFinanceCategory?.detailed,
    ].filter(Boolean).join(' / '),
  }));
  const largeRows = (result.largeTransactions || []).map(txn => ({
    date: txn.date,
    amount: txn.amount,
    rawAmount: txn.rawAmount,
    direction: txn.direction,
    classifiedAsTransfer: txn.classifiedAsTransfer,
    classificationReason: txn.classificationReason || '',
    accountName: txn.accountName,
    accountType: txn.accountType,
    accountSubtype: txn.accountSubtype,
    category: txn.category?.join(' / ') || '',
    personalFinanceCategory: [
      txn.personalFinanceCategory?.primary,
      txn.personalFinanceCategory?.detailed,
    ].filter(Boolean).join(' / '),
  }));

  console.groupCollapsed(`[DYR transfer debug] Plaid cash transactions item ${itemId}`);
  console.log('Date range', dateRange || null);
  console.log('Transactions scanned', result.totalTransactions);
  console.log('Transfer candidates returned', result.candidateTransactions);
  console.log('[DYR transfer debug JSON] cash transfer candidates', JSON.stringify(rows.slice(0, 100)));
  console.log('Large raw cash transactions', largeRows.length);
  console.log('[DYR transfer debug JSON] large raw cash transactions', JSON.stringify(largeRows.slice(0, 100)));
  console.table(rows.slice(0, 100));
  console.table(largeRows.slice(0, 100));
  if (rows.length > 100) {
    console.log(`Showing first 100 of ${rows.length} cash transfer candidates`);
  }
  if (largeRows.length > 100) {
    console.log(`Showing first 100 of ${largeRows.length} large raw cash transactions`);
  }
  console.groupEnd();
}

function shouldLogTransferDebug(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('debugTransfers') === '1' || localStorage.getItem(TRANSFER_DEBUG_KEY) === '1';
}

function isPotentialTransferActivity(txn: PlaidTransaction, activity: AccountActivity | undefined): boolean {
  const raw = `${txn.type || ''} ${txn.subtype || ''}`.toLowerCase();
  const mapped = String(activity?.type || '').toUpperCase();
  return (
    raw.includes('cash') ||
    raw.includes('transfer') ||
    raw.includes('deposit') ||
    raw.includes('withdraw') ||
    raw.includes('contribution') ||
    raw.includes('distribution') ||
    raw.includes('pending') ||
    raw.includes('adjustment') ||
    raw.includes('send') ||
    raw.includes('request') ||
    mapped === 'DEPOSIT' ||
    mapped === 'WITHDRAWAL' ||
    mapped.includes('TRANSFER')
  );
}

function mapToSortedRows(map: Map<string, number>, keyName: string): Array<Record<string, number | string>> {
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, count]) => ({ [keyName]: key, count }));
}

function maskAccount(value: string | null | undefined): string {
  if (!value) return '';
  const suffix = value.slice(-4);
  return suffix ? `*${suffix}` : '';
}

// ============================================================================
// Disconnect
// ============================================================================

export async function disconnectBrokerage(
  visitorId: string,
  itemId: string
): Promise<void> {
  await workerDelete(
    `/plaid/disconnect?visitorId=${encodeURIComponent(visitorId)}&itemId=${encodeURIComponent(itemId)}`
  );

  removeStoredItemId(itemId);
}

// ============================================================================
// Cost Basis Overrides (reuse existing structure)
// ============================================================================

export interface CostBasisOverride {
  activityId: string;
  price: number;
  symbol: string;
  units: number;
  tradeDate: string;
  savedAt: string;
  notes?: string | null;
}

interface CostBasisOverridesResponse {
  overrides: Record<string, CostBasisOverride>;
}

export async function getCostBasisOverrides(
  accountId: string
): Promise<Map<string, CostBasisOverride>> {
  const visitorId = getStoredVisitorId();
  if (!visitorId) return new Map();

  try {
    // Reuse the SnapTrade cost basis endpoint for now
    const result = await workerGet<CostBasisOverridesResponse>(
      `/snaptrade/costbasis?visitorId=${encodeURIComponent(visitorId)}&accountId=${encodeURIComponent(accountId)}`
    );
    return new Map(Object.entries(result.overrides || {}));
  } catch (error) {
    console.error('Failed to fetch cost basis overrides:', error);
    return new Map();
  }
}

export async function saveCostBasisOverride(
  accountId: string,
  override: Omit<CostBasisOverride, 'savedAt'>
): Promise<boolean> {
  const visitorId = getStoredVisitorId();
  if (!visitorId) return false;

  try {
    await workerPost(
      `/snaptrade/costbasis?visitorId=${encodeURIComponent(visitorId)}&accountId=${encodeURIComponent(accountId)}`,
      override
    );
    return true;
  } catch (error) {
    console.error('Failed to save cost basis override:', error);
    return false;
  }
}

export async function deleteCostBasisOverride(
  accountId: string,
  activityId: string
): Promise<boolean> {
  const visitorId = getStoredVisitorId();
  if (!visitorId) return false;

  try {
    await workerDelete(
      `/snaptrade/costbasis?visitorId=${encodeURIComponent(visitorId)}&accountId=${encodeURIComponent(accountId)}&activityId=${encodeURIComponent(activityId)}`
    );
    return true;
  } catch (error) {
    console.error('Failed to delete cost basis override:', error);
    return false;
  }
}
